import { Result, Risky } from '@rotomeca/rop';
import { Nullable } from '@rotomeca/utils';
import { EMPTY_STRING } from '@rotomeca/utils';

export enum BnumDateLocale {
	FR = 'fr-FR',
	EN = 'en-US',
}

/**
 * Native replacements for date-fns functions to reduce bundle size.
 * Uses Intl API for localization and native Date for manipulations.
 */
export class BnumDateUtils {
	/**
	 * Equivalent to date-fns/format.
	 * @param date Date to format.
	 * @param options Intl options or a simple locale string.
	 * @param locale Locale string (e.g., 'fr-FR', 'en-US').
	 */
	static format(
		date: Date,
		options: Intl.DateTimeFormatOptions = {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
		},
		locale: string = 'fr-FR',
	): string {
		return new Intl.DateTimeFormat(locale, options).format(date);
	}

	/**
	 * Parse une chaîne de caractères en objet Date.
	 * @param dateString La chaîne à parser (ex: "12/08/1997")
	 * @param format Optionnel : le format de la chaîne (ex: "dd/MM/yyyy")
	 */
	static parse(dateString: string, format?: string): Nullable<Date> {
		return this.#_parse(dateString, format).unwrapOr(null);
	}

	/**
	 * Parse une chaîne de caractères en objet Date.
	 * @param dateString La chaîne à parser (ex: "12/08/1997")
	 * @param format Optionnel : le format de la chaîne (ex: "dd/MM/yyyy")
	 */
	@Risky()
	static #_parse(dateString: string, format?: string): Result<Nullable<Date>> {
		if (!dateString) return null as any;

		// 1. Si aucun format n'est fourni, on tente le parsing natif (ISO 8601)
		if (!format) {
			const d = new Date(dateString);
			return (this.isValid(d) ? d : null) as any;
		} else {
			if (['PPPP', 'PPP', 'PP', 'P'].includes(format)) {
				format = 'dd/MM/yyyy';
			}
		}

		// On extrait les nombres de la chaîne (ignore les séparateurs comme / - :)
		const values = dateString.match(/\d+/g);
		const tokens = format.match(/[a-zA-Z]+/g);

		if (!values || !tokens || values.length !== tokens.length) {
			if (values && tokens && values.length < tokens.length) {
				for (let index = values.length; index < tokens.length; ++index) {
					values.push(
						Array.from(tokens[index])
							.map(() => '0')
							.join(EMPTY_STRING),
					);
				}
			} else return null as any;
		}

		let year = new Date().getFullYear();
		let month = 0;
		let day = 1;
		let hour = 0;
		let minute = 0;

		tokens.forEach((token, index) => {
			const val = parseInt(values[index], 10);
			if (token.includes('y')) year = val;
			if (token.includes('M')) month = val - 1; // Mois 0-11 en JS
			if (token.includes('d')) day = val;
			if (token.includes('H')) hour = val;
			if (token.includes('m')) minute = val;
		});

		const result = new Date(year, month, day, hour, minute);
		return (this.isValid(result) ? result : null) as any;
	}

	/**
	 * Equivalent to date-fns/isValid.
	 */
	static isValid(date: any): date is Date {
		return date instanceof Date && !isNaN(date.getTime());
	}

	/**
	 * Equivalent to date-fns/addDays (Immutable).
	 */
	static addDays(date: Date, days: number): Date {
		const result = new Date(date);
		result.setDate(result.getDate() + days);
		return result;
	}

	/**
	 * Equivalent to date-fns/addMonths (Immutable).
	 */
	static addMonths(date: Date, months: number): Date {
		const result = new Date(date);
		result.setMonth(result.getMonth() + months);
		return result;
	}

	/**
	 * Equivalent to date-fns/addYears (Immutable).
	 */
	static addYears(date: Date, years: number): Date {
		const result = new Date(date);
		result.setFullYear(result.getFullYear() + years);
		return result;
	}

	/**
	 * Convertit dynamiquement une chaîne de tokens (ex: "dd/MM") en options Intl.
	 * @param pattern La chaîne de formatage.
	 */
	static getOptionsFromToken(pattern: string): Intl.DateTimeFormatOptions {
		if (pattern.includes('PPPP')) return { dateStyle: 'full' };
		if (pattern.includes('PPP')) return { dateStyle: 'long' };
		if (pattern.includes('PP')) return { dateStyle: 'medium' };
		if (pattern.includes('P')) return { dateStyle: 'short' };

		const options: Intl.DateTimeFormatOptions = {};

		if (pattern.includes('yyyy')) options.year = 'numeric';
		else if (pattern.includes('yy')) options.year = '2-digit';

		if (pattern.includes('MMMM')) options.month = 'long';
		else if (pattern.includes('MMM')) options.month = 'short';
		else if (pattern.includes('MM')) options.month = '2-digit';
		else if (pattern.includes('M')) options.month = 'numeric';

		if (pattern.includes('dd')) options.day = '2-digit';
		else if (pattern.includes('d')) options.day = 'numeric';

		if (pattern.includes('EEEE')) options.weekday = 'long';
		else if (pattern.includes('E')) options.weekday = 'short';

		if (pattern.includes('HH')) options.hour = '2-digit';
		else if (pattern.includes('H')) options.hour = 'numeric';

		if (pattern.includes('mm')) options.minute = '2-digit';

		// Force 24h si on demande des heures
		if (options.hour) options.hour12 = false;

		return options;
	}

	/**
	 * Vérifie si deux dates correspondent au même jour (ignore l'heure).
	 * @param date Première date à comparer.
	 * @param now Deuxième date à comparer (par défaut : Date actuelle).
	 * @returns True si c'est le même jour.
	 */
	static isSameDay(date: Date, now: Date = new Date()): boolean {
		return (
			date.getFullYear() === now.getFullYear() &&
			date.getMonth() === now.getMonth() &&
			date.getDate() === now.getDate()
		);
	}

	/**
	 * Vérifie si la date fournie est aujourd'hui.
	 */
	static isToday(date: Date): boolean {
		return this.isSameDay(date, new Date());
	}

	/**
	 * Retourne une nouvelle date fixée au début du jour (00:00:00.000).
	 */
	static startOfDay(date: Date): Date {
		const result = new Date(date);
		result.setHours(0, 0, 0, 0);
		return result;
	}

	/**
	 * Retourne une nouvelle date fixée à la fin du jour (23:59:59.999).
	 */
	static endOfDay(date: Date): Date {
		const result = new Date(date);
		result.setHours(23, 59, 59, 999);
		return result;
	}

	/**
	 * Soustrait un nombre de jours à une date (Immuable).
	 */
	static subDays(date: Date, amount: number): Date {
		return this.addDays(date, -amount);
	}

	/**
	 * Vérifie si une date se trouve dans un intervalle donné (inclusif).
	 * @param date Date à vérifier.
	 * @param interval Objet contenant start et end.
	 */
	static isWithinInterval(
		date: Date,
		interval: { start: Date; end: Date },
	): boolean {
		const time = date.getTime();
		return time >= interval.start.getTime() && time <= interval.end.getTime();
	}
}
